import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { analyticsAPI, capsulesAPI, capsuleConfigAPI, ticketsAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  BarChart3, Loader2, RefreshCw, AlertTriangle, Clock, Users, Building2, Save, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { CatalogSelect } from '@/components/RoutingMetadataForm';
import { unidadDeTrabajo } from '@/hooks/useCapsuleRoutingCatalogs';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarraDeSemaforo, ChartCard, ComoSeCalcula, CustomTooltip, KpiCard, colorDeSerie,
} from '@/components/charts';
import PanelDesvio from '@/components/executive/PanelDesvio';
import PanelAnalisis from '@/components/executive/PanelAnalisis';
import PrioridadDeLaEmpresa from '@/components/executive/PrioridadDeLaEmpresa';
import CategoriasPage from '@/pages/CategoriasPage';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { fechaYHora } from '../lib/fechas';

/**
 * Que significa «esperando» segun como la capsula declaro que se detiene el reloj de atencion.
 *
 * **Que se rompio.** Decia *"nadie se hizo cargo todavia"*, que se lee como **propiedad**. El
 * 2026-10-02, probando: un caso con propietario, con tareas asignadas y con el dueño traspasado
 * figuraba igual en «Esperando ahora», y la pregunta fue por que. La respuesta es que el reloj de
 * atencion de esta capsula se detiene con `mensaje_humano`: lo que mide es si **quien pidio recibio
 * señales de vida de una persona**, y tomar el caso no es una señal de vida para quien espera.
 *
 * El numero estaba bien; la frase medía otra cosa. Y como el modo es configuracion, la frase sale
 * del modo: una leyenda fija miente en la capsula de al lado.
 */
const ESPERANDO_SEGUN_MODO = {
  mensaje_humano: 'quien pidió todavía no recibió respuesta de una persona',
  estado: 'el caso no entró todavía en un estado que cuente como atendido',
  lo_primero_que_ocurra: 'todavía no hubo respuesta ni cambio de estado que cuente',
};

function subDeEsperando(modo) {
  return ESPERANDO_SEGUN_MODO[modo] || 'nadie dio señales de vida a quien pidió';
}

/**
 * Las frases de la parametrizacion vigente, armadas una vez por carga.
 *
 * **No son texto fijo.** Un cumplimiento significa cosas distintas segun el SLA de cada tipo de
 * pedido, segun el horario del area y segun como esa capsula detiene su reloj de atencion. El
 * backend manda esos parametros junto a los numeros (`aging.parametros`), igual que el Gantt manda
 * `meta.horas_por_dia` — y por el mismo motivo: una ayuda escrita a mano queda vieja el dia que
 * alguien cambia la configuracion, y nadie se entera.
 */
function frasesDeLaParametrizacion(p) {
  if (!p) return {};
  const modos = {
    mensaje_humano: 'un mensaje de una persona a quien pidió',
    estado: 'que el caso entre en un estado marcado como atendido',
    lo_primero_que_ocurra: 'lo primero que ocurra: un mensaje o un cambio de estado',
  };
  return {
    atencion: (
      <>
        <b>Configuración de Cápsula → Intents</b>: las horas de atención de cada tipo de pedido, y
        qué detiene el reloj — en esta cápsula,{' '}
        <b>{modos[p.modo_de_atencion] || p.modo_de_atencion}</b>.
      </>
    ),
    resolucion: (
      <>
        <b>Configuración de Cápsula → Intents</b>: las horas de resolución de cada tipo de pedido.
        Hoy las declaran <b>{p.intents_con_sla_de_resolucion}</b> de {p.intents}.
      </>
    ),
    horario: (
      <>
        <b>Configuración de Cápsula → horario</b>: {p.jornada}, {p.dias_habiles?.length} días por
        semana{p.pausa_minutos ? `, con ${p.pausa_minutos} min de pausa` : ''}.
      </>
    ),
    // La asimetría que ninguna pantalla decía, y que explica dos números que parecen no cerrar.
    pausa: p.pausa_minutos ? (
      <>
        La pausa de <b>{p.pausa_minutos} min</b> resta del <b>esfuerzo</b> —son {p.horas_por_dia} h
        de trabajo por día, y de ahí sale la duración de una tarea— y <b>no</b> resta del reloj del
        SLA, que corre en horario de atención. Es deliberado: son dos preguntas distintas.
      </>
    ) : null,
  };
}

const SYSTEM_PRESETS = [
  { id: 'open', name: 'Abiertos', filters: { status_scope: 'open', priority: '', priority_in: '', status: '', department: '' } },
  { id: 'critical', name: 'Críticos', filters: { status_scope: 'open', priority: '', priority_in: 'high,critical', status: '', department: '' } },
  { id: 'overdue', name: 'Vencidos', filters: { status_scope: 'open', priority: '', priority_in: '', status: '', department: '', overdue_only: true } },
  { id: 'by_dept', name: 'Por gerencia', filters: { status_scope: 'open', priority: '', priority_in: '', status: '', department: '' } },
];

const NOTE_MAX = 240;

function Kpi({ label, value, sub, danger, ayuda }) {
  return (
    <Card className="rounded-sm border-brand-border">
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className={`text-2xl font-bold mt-1 ${danger ? 'text-brand-danger' : 'text-[#09090B]'}`}>{value}</div>
        {sub && <div className="text-[10px] text-zinc-400 mt-0.5">{sub}</div>}
        {ayuda}
      </CardContent>
    </Card>
  );
}

function ageHours(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((Date.now() - t) / 3600000));
}

function buildListParams(filters) {
  const p = {};
  if (filters.status_scope) p.status_scope = filters.status_scope;
  else if (filters.status) p.status = filters.status;
  if (filters.priority_in) p.priority_in = filters.priority_in;
  else if (filters.priority) p.priority = filters.priority;
  if (filters.department) p.requesting_department = filters.department;
  if (filters.note_q) p.note_q = filters.note_q;
  // **El vencimiento lo filtra el servidor, contra `fecha_hasta`.** El preset "Vencidos" cruzaba
  // ids de una lista que se medía contra `due_date` —campo que ninguna pantalla escribe— y devolvia
  // una cola vacia siempre. `vence` es el parametro que la bandeja de casos ya usa bien.
  if (filters.overdue_only) p.vence = 'vencidos';
  return p;
}

function buildAnalyticsParams(filters) {
  const p = {};
  if (filters.capsule_id) p.capsule_id = filters.capsule_id;
  if (filters.department) p.department = filters.department;
  if (filters.status_scope) p.status_scope = filters.status_scope;
  else if (filters.status) p.status = filters.status;
  if (filters.priority_in) p.priority_in = filters.priority_in;
  else if (filters.priority) p.priority = filters.priority;
  return p;
}

function buildGanttSearch(filters, extra = {}) {
  const p = new URLSearchParams();
  if (filters.capsule_id) p.set('capsule', filters.capsule_id);
  if (filters.department) p.set('department', filters.department);
  if (filters.status_scope) p.set('status_scope', filters.status_scope);
  else if (filters.status) p.set('status', filters.status);
  if (filters.priority_in) p.set('priority_in', filters.priority_in);
  else if (filters.priority) p.set('priority', filters.priority);
  Object.entries(extra).forEach(([k, v]) => { if (v) p.set(k, v); });
  const q = p.toString();
  return `/executive/gantt${q ? `?${q}` : ''}`;
}

function noteHistory(ticket) {
  const tl = ticket.timeline || [];
  return tl
    .filter((e) => e.action === 'management_note')
    .map((e) => ({
      text: e.details?.text || '',
      at: e.timestamp,
      by: e.actor_name || e.actor_id || '—',
    }))
    .reverse();
}

export default function ExecutivePage() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const capsuleFromUrl = searchParams.get('capsule') || '';

  const [capsules, setCapsules] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [departments, setDepartments] = useState([]);
  // Como llama esta capsula a lo que trabaja: mesa de ayuda dice ticket, mantenimiento
  // diria orden. La pantalla no puede tener "Ticket" escrito.
  const [unidad, setUnidad] = useState(null);
  const [filters, setFilters] = useState({
    capsule_id: capsuleFromUrl,
    department: '',
    priority: '',
    priority_in: '',
    status: '',
    status_scope: 'open',
    overdue_only: false,
    note_q: '',
  });
  const [activeViewId, setActiveViewId] = useState('open');
  const [savedViews, setSavedViews] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [savingView, setSavingView] = useState(false);

  const [summary, setSummary] = useState(null);
  const [byDept, setByDept] = useState([]);
  const [byPriority, setByPriority] = useState([]);
  const [byStatus, setByStatus] = useState([]);
  const [workload, setWorkload] = useState([]);
  const [aging, setAging] = useState(null);
  // Lo que el semaforo de SLA no puede contestar: con el plan que hay, que se va a incumplir.
  const [plan, setPlan] = useState(null);
  const [desvioEnCurso, setDesvioEnCurso] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queueLoading, setQueueLoading] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  const [tab, setTab] = useState(searchParams.get('tab') || 'resumen');
  const [desvio, setDesvio] = useState(null);
  const [desvioLoading, setDesvioLoading] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    // **Con una sola cápsula, se elige sola (2026-10-09).** La pantalla arrancaba sin cápsula salvo
    // que viniera en el link, y todas las solapas —Prioridades incluida— decían «elegí una cápsula»
    // sobre un selector con una única opción: quien entró a priorizar no encontró los casos.
    capsulesAPI.list(tenantId).then((res) => {
      const lista = res.data || [];
      setCapsules(lista);
      if (lista.length === 1) {
        setFilters((f) => (f.capsule_id ? f : { ...f, capsule_id: lista[0].capsule_id }));
      }
    }).catch(() => {});
    analyticsAPI.executiveSavedViews()
      .then((res) => setSavedViews(res.data || []))
      .catch(() => setSavedViews([]));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !filters.capsule_id) {
      setDepartments([]);
      setPriorities([]);
      setStatuses([]);
      setUnidad(null);
      return;
    }
    capsuleConfigAPI.get(tenantId, filters.capsule_id)
      .then((res) => {
        const cfg = res.data || {};
        setDepartments(cfg.requesting_departments || []);
        setPriorities(cfg.priorities || []);
        setStatuses(cfg.ticket_statuses || []);
        setUnidad(cfg.unidad_de_trabajo || null);
      })
      .catch(() => {
        setDepartments([]);
        setPriorities([]);
        setStatuses([]);
        setUnidad(null);
      });
  }, [tenantId, filters.capsule_id]);

  const analyticsParams = useCallback(() => buildAnalyticsParams(filters), [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = analyticsParams();
    try {
      const [s, d, pr, st, wl, ag, pc, dec] = await Promise.all([
        analyticsAPI.pmoSummary(params),
        analyticsAPI.pmoByDepartment(params),
        analyticsAPI.pmoByPriority(params),
        analyticsAPI.pmoByStatus(params),
        // Recibe **los filtros de la pantalla**: antes mandaba solo la cápsula, así que con
        // «Críticos» puesto la carga seguía siendo la de todo el mundo, sin decirlo.
        analyticsAPI.pmoWorkload(params),
        analyticsAPI.pmoAging(params),
        analyticsAPI.pmoPlanVsCompromiso(params),
        // Es por tarea y no por caso, asi que solo acota la capsula: los filtros de la pantalla
        // —gerencia, prioridad, estado— son del caso. Que ignora el resto lo dice el KPI.
        analyticsAPI.pmoDesvioEnCurso(params.capsule_id ? { capsule_id: params.capsule_id } : undefined),
      ]);
      setSummary(s.data); setByDept(d.data || []); setByPriority(pr.data || []);
      setByStatus(st.data || []); setWorkload(wl.data || []); setAging(ag.data);
      setPlan(pc.data); setDesvioEnCurso(dec.data);
    } catch {
      toast.error('No se pudieron cargar las metricas');
    } finally {
      setLoading(false);
    }
  }, [analyticsParams]);

  // El desvio se carga solo cuando se abre su pestaña: es la consulta mas cara de las
  // cuatro y la mayoria de las visitas se quedan en Resumen.
  const loadDesvio = useCallback(async () => {
    setDesvioLoading(true);
    try {
      // El desvio **ignora el filtro de estado** a proposito. "Entregado en fecha" se mide
      // sobre lo que termino, y el filtro por defecto es "Abiertos": con el aplicado, ese
      // KPI no puede tener datos nunca, y un guion permanente se lee como "no medimos" en
      // vez de "filtraste justo lo que hace falta". El panel lo dice en su subtitulo.
      const { status_scope: _s, status: _st, ...sinEstado } = analyticsParams();
      const r = await analyticsAPI.pmoDesvio(sinEstado);
      setDesvio(r.data);
    } catch (e) {
      setDesvio(null);
      toast.error(formatApiError(e));
    } finally {
      setDesvioLoading(false);
    }
  }, [analyticsParams]);

  useEffect(() => { if (tab === 'desvio') loadDesvio(); }, [tab, loadDesvio]);

  const loadQueue = useCallback(async () => {
    if (!tenantId || !filters.capsule_id) {
      setQueue([]);
      return;
    }
    setQueueLoading(true);
    try {
      const params = { ...buildListParams(filters), limit: 80 };
      const res = await ticketsAPI.list(tenantId, filters.capsule_id, params);
      // **El preset "Vencidos" devolvia una cola vacia, siempre.** Cruzaba los ids que trae
      // `aging.overdue` —que se median contra `due_date`, un campo que ninguna pantalla escribe— y
      // si venia vacio caia a filtrar por ese mismo campo en el cliente. Las dos ramas daban cero.
      // Un gerente apretaba, no veia nada, y concluia que no habia nada vencido.
      //
      // Ahora lo contesta el servidor con el parametro que ya existia y funcionaba bien en la
      // bandeja de casos: `vence=vencidos`, contra `fecha_hasta`. Una sola definicion de "esto se
      // vencio" en todo el producto.
      setQueue(res.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
      setQueue([]);
    } finally {
      setQueueLoading(false);
    }
    // `aging` salio de las dependencias: la cola ya no depende de lo que esa consulta devuelva.
  }, [tenantId, filters]);

  useEffect(() => { if (tenantId) load(); }, [tenantId, load]);
  useEffect(() => { if (tenantId) loadQueue(); }, [tenantId, loadQueue]);

  const applyPreset = (preset) => {
    setActiveViewId(preset.id);
    setFilters((f) => ({
      ...f,
      ...preset.filters,
      overdue_only: preset.filters.overdue_only || false,
      note_q: f.note_q,
      capsule_id: f.capsule_id,
    }));
  };

  const applySavedView = (view) => {
    setActiveViewId(view.view_id);
    const vf = view.filters || {};
    // Migrar vistas legacy open: / comma
    let status_scope = vf.status_scope || '';
    let status = vf.status || '';
    let priority_in = vf.priority_in || '';
    let priority = vf.priority || '';
    if (status === 'open:' || (!status_scope && status === 'open:')) {
      status_scope = 'open';
      status = '';
    }
    if (priority && priority.includes(',')) {
      priority_in = priority;
      priority = '';
    }
    setFilters((f) => ({
      capsule_id: vf.capsule_id || f.capsule_id,
      department: vf.department || '',
      priority,
      priority_in,
      status,
      status_scope: status_scope || (status ? '' : 'open'),
      overdue_only: !!vf.overdue_only,
      note_q: vf.note_q || '',
    }));
  };

  const handleSaveView = async () => {
    if (!saveName.trim()) return;
    setSavingView(true);
    try {
      const res = await analyticsAPI.createExecutiveSavedView({
        name: saveName.trim(),
        filters: {
          capsule_id: filters.capsule_id || '',
          department: filters.department || '',
          priority: filters.priority || '',
          priority_in: filters.priority_in || '',
          status: filters.status || '',
          status_scope: filters.status_scope || '',
          overdue_only: !!filters.overdue_only,
          note_q: filters.note_q || '',
        },
      });
      setSavedViews((v) => [res.data, ...v].slice(0, 5));
      setSaveName('');
      setActiveViewId(res.data.view_id);
      toast.success('Vista guardada');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingView(false);
    }
  };

  const handleDeleteView = async (viewId) => {
    try {
      await analyticsAPI.deleteExecutiveSavedView(viewId);
      setSavedViews((v) => v.filter((x) => x.view_id !== viewId));
      if (activeViewId === viewId) setActiveViewId('open');
      toast.success('Vista eliminada');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const patchPriority = async (ticket, priority) => {
    try {
      await ticketsAPI.patchRoutingMetadata(tenantId, ticket.capsule_id || filters.capsule_id, ticket.ticket_id, {
        priority,
        sync_conversations: true,
      });
      setQueue((rows) => rows.map((r) => (r.ticket_id === ticket.ticket_id ? { ...r, priority } : r)));
      toast.success('Prioridad actualizada');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addNote = async (ticket) => {
    const text = (noteDrafts[ticket.ticket_id] || '').trim();
    if (!text) return;
    if (text.length > NOTE_MAX) {
      toast.error(`Nota máx ${NOTE_MAX} caracteres`);
      return;
    }
    try {
      const res = await ticketsAPI.addManagementNote(tenantId, ticket.capsule_id || filters.capsule_id, ticket.ticket_id, text);
      const last = res.data?.last_management_note;
      setNoteDrafts((d) => ({ ...d, [ticket.ticket_id]: '' }));
      setExpandedId(null);
      if (last) {
        setQueue((rows) => rows.map((r) => (
          r.ticket_id === ticket.ticket_id
            ? {
              ...r,
              last_management_note: last,
              timeline: [...(r.timeline || []), res.data.event].filter(Boolean),
            }
            : r
        )));
      }
      toast.success('Nota agregada');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const maxDept = Math.max(1, ...byDept.map((d) => d.count));

  const priorityLabel = useMemo(() => {
    const map = Object.fromEntries((priorities || []).map((p) => [p.id, p.name || p.id]));
    return (id) => map[id] || id || '—';
  }, [priorities]);

  const statusLabel = useMemo(() => {
    const map = Object.fromEntries((statuses || []).map((s) => [s.id, s.name || s.id]));
    return (id) => map[id] || id || '—';
  }, [statuses]);

  const openPersonGantt = (personId) => {
    navigate(buildGanttSearch(filters, { person_id: personId }));
  };

  const atn = aging?.sla_atencion || {};
  // Las frases de la configuración vigente, una vez por carga y no una por tarjeta.
  const dice = frasesDeLaParametrizacion(aging?.parametros);
  const sla = aging?.sla || {};

  return (
    <div className="p-6 space-y-5 overflow-auto h-full" data-testid="executive-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#09090B] flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Tablero Ejecutivo</h1>
          <p className="text-xs text-zinc-500">Atención, cola accionable, distribución y SLA real.</p>
        </div>
        <div className="flex gap-2">
          <Link to={buildGanttSearch(filters)}>
            <Button size="sm" className="rounded-sm h-8 text-xs" data-testid="executive-gantt-btn">
              Ver Gantt
            </Button>
          </Link>
          <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs" onClick={() => { load(); loadQueue(); }} disabled={loading}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </Button>
        </div>
      </div>

      <Card className="rounded-sm border-brand-border">
        <CardContent className="p-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-xs">
          <div>
            <label className="text-[10px] text-zinc-500">Capsula</label>
            <select
              className="w-full border border-brand-border rounded-sm h-8 px-2 text-xs mt-0.5 bg-white"
              value={filters.capsule_id}
              onChange={(e) => setFilters((f) => ({ ...f, capsule_id: e.target.value, department: '' }))}
            >
              <option value="">Elegí capsula (requerida para cola/catalogo)</option>
              {capsules.map((c) => <option key={c.capsule_id} value={c.capsule_id}>{c.name || c.capsule_id}</option>)}
            </select>
          </div>
          <CatalogSelect
            label="Gerencia"
            value={filters.department}
            onChange={(v) => setFilters((f) => ({ ...f, department: v }))}
            items={departments}
            placeholder={filters.capsule_id ? 'Todas' : 'Elegí capsula primero'}
            disabled={!filters.capsule_id}
          />
          <CatalogSelect
            label="Prioridad"
            value={filters.priority_in ? '' : (filters.priority || '')}
            onChange={(v) => setFilters((f) => ({ ...f, priority: v, priority_in: '', overdue_only: false }))}
            items={priorities}
            placeholder={filters.capsule_id ? (filters.priority_in ? 'Críticos (vista)' : 'Todas') : 'Elegí capsula'}
            disabled={!filters.capsule_id}
          />
          <CatalogSelect
            label="Estado"
            value={filters.status_scope === 'open' ? '' : (filters.status || '')}
            onChange={(v) => setFilters((f) => ({
              ...f,
              status: v,
              status_scope: v ? '' : 'open',
              overdue_only: false,
            }))}
            items={statuses}
            placeholder={filters.status_scope === 'open' ? 'Abiertos (vista)' : 'Todos'}
            disabled={!filters.capsule_id}
          />
          <div>
            <label className="text-[10px] text-zinc-500">Buscar en notas</label>
            <Input
              className="h-8 text-xs rounded-sm mt-0.5"
              placeholder="note_q…"
              value={filters.note_q || ''}
              onChange={(e) => setFilters((f) => ({ ...f, note_q: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap items-center gap-2" data-testid="executive-saved-views">
        {SYSTEM_PRESETS.map((p) => (
          <Button
            key={p.id}
            size="sm"
            variant={activeViewId === p.id ? 'default' : 'outline'}
            className="h-7 text-xs rounded-sm"
            onClick={() => applyPreset(p)}
          >
            {p.name}
          </Button>
        ))}
        {savedViews.map((v) => (
          <div key={v.view_id} className="inline-flex items-center gap-0.5">
            <Button
              size="sm"
              variant={activeViewId === v.view_id ? 'default' : 'outline'}
              className="h-7 text-xs rounded-sm"
              onClick={() => applySavedView(v)}
            >
              {v.name}
            </Button>
            <button type="button" className="text-zinc-400 hover:text-red-500 p-0.5" onClick={() => handleDeleteView(v.view_id)} title="Eliminar vista">
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-1 ml-auto">
          <Input
            className="h-7 text-xs rounded-sm w-40"
            placeholder="Nombre de vista"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
          />
          <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" disabled={savingView || !saveName.trim()} onClick={handleSaveView}>
            <Save className="w-3 h-3 mr-1" /> Guardar vista
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-zinc-100 rounded-sm h-9" data-testid="executive-tabs">
          {[['resumen', 'Resumen'], ['analisis', 'Análisis'], ['desvio', 'Desvío'], ['categorias', 'Categorías'], ['prioridades', 'Prioridades']].map(
            ([id, label]) => (
              <TabsTrigger key={id} value={id} className="text-xs rounded-sm data-[state=active]:bg-white">
                {label}
              </TabsTrigger>
            )
          )}
        </TabsList>

        <TabsContent value="resumen" className="space-y-5 mt-4">
      {/* Atención: el reloj que hasta hoy no se medía en ningún lado. */}
      {/* **El filtro que esta fila ignora, dicho.** Los cuatro números de abajo se calculan
          siempre sobre los **abiertos**, aunque arriba haya otro estado elegido: un reloj que corre
          sobre un caso cerrado no mide nada. Era el único grupo del tablero que lo ignoraba en
          silencio; el patrón de decirlo lo tenía sólo el panel de Desvío. */}
      {(filters.status || (filters.status_scope && filters.status_scope !== "open")) && (
        <p className="text-[11px] text-amber-700" data-testid="atencion-ignora-estado">
          Los relojes se miden siempre sobre los casos <b>abiertos</b>: el filtro de estado que
          tenés puesto no se aplica a esta fila.
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="executive-attention">
        <KpiCard
          label="Atendido en plazo"
          value={atn.cumplimiento_pct == null ? '—' : `${atn.cumplimiento_pct}%`}
          sub={`sobre ${atn.medidas || 0} con reloj de atención`}
          danger={atn.cumplimiento_pct != null && atn.cumplimiento_pct < 80}
          ayuda={
            <ComoSeCalcula
              testId="ayuda-atendido-en-plazo"
              pregunta="¿Cuántos de los casos que miro recibieron una señal de vida dentro del plazo?"
              cuenta="Horas hábiles entre el primer mensaje de quien pidió y la primera señal de vida, contra las horas de atención que declara el tipo de pedido."
              universo={
                <>
                  {atn.medidas || 0} casos con reloj de atención.{' '}
                  {(atn.sin_reloj || 0) > 0 && (
                    <>
                      Quedan afuera <b>{atn.sin_reloj}</b> sin reloj —su tipo de pedido no declara
                      horas de atención, o el caso no tiene primer contacto—: no se cuentan como
                      cumplidos ni como incumplidos.
                    </>
                  )}
                </>
              }
              parametro={dice.atencion}
              ojo={dice.pausa}
            />
          }
        />
        <KpiCard
          label="Esperando ahora"
          value={aging?.sla_atencion?.esperando_total ?? '—'}
          sub={subDeEsperando(atn.modo)}
          danger={(aging?.sla_atencion?.esperando_total || 0) > 0}
          ayuda={
            <ComoSeCalcula
              testId="ayuda-esperando-ahora"
              pregunta="¿A cuántos todavía no les contestó nadie?"
              cuenta="Casos abiertos cuyo reloj de atención sigue corriendo: nunca recibieron lo que esta cápsula declaró como señal de vida."
              universo="Casos abiertos con primer contacto. Sin primer contacto no hay reloj y no figuran."
              parametro={dice.atencion}
              ojo={
                <>
                  <b>No tiene nada que ver con quién es el dueño.</b> Un caso puede tener
                  propietario, tareas repartidas y haber cambiado de dueño dos veces, y seguir acá:
                  tomar un caso no es una señal de vida para quien lo pidió.
                </>
              }
            />
          }
        />
        <KpiCard
          label="Fuera de SLA de resolución"
          value={sla.breach ?? '—'}
          danger={(sla.breach || 0) > 0}
          sub="reloj hábil ≥ SLA del intent"
          ayuda={
            <ComoSeCalcula
              testId="ayuda-fuera-de-sla"
              pregunta="¿Cuántos casos ya consumieron todo el tiempo de trabajo que prometimos?"
              cuenta="Se suman las horas hábiles en que el reloj estuvo corriendo —se detiene al entrar en un estado que lo detiene, y se reanuda si el caso se reabre— y se comparan con las horas de resolución del tipo de pedido."
              universo={
                <>
                  Casos abiertos con reloj de resolución.{' '}
                  {(sla.sin_reloj || 0) > 0 && (
                    <>
                      <b>{sla.sin_reloj}</b> no tienen reloj: su tipo de pedido no promete cierre, y
                      eso es legítimo —un proyecto se mide por hitos—.
                    </>
                  )}
                </>
              }
              parametro={dice.resolucion}
              ojo={dice.pausa}
            />
          }
        />
        <KpiCard
          label="Vencidos (fecha)"
          value={aging?.overdue_total ?? '—'}
          danger={(aging?.overdue_total || 0) > 0}
          sub="pasaron la fecha comprometida con quien pidió"
          ayuda={
            <ComoSeCalcula
              testId="ayuda-vencidos"
              pregunta="¿A cuántos ya les pasamos la fecha que les prometimos?"
              cuenta="La fecha comprometida del caso contra ahora. Es una fecha absoluta, no horas de trabajo: es la mirada de quien espera, no la del equipo."
              universo="Casos abiertos con una fecha comprometida. Un caso sin compromiso no está vencido: está sin medir, y aparece en «Sin compromiso» del panel de Desvío."
              parametro={
                <>
                  La fecha sale del SLA de resolución del tipo de pedido aplicado sobre el horario
                  del área, y se puede renegociar a mano desde el caso —queda registrado quién y por
                  qué, y deja de recalcularse—.
                </>
              }
              ojo={
                <>
                  <b>No es lo mismo que «Fuera de SLA de resolución».</b> Ése mide horas de trabajo
                  consumidas; éste, una fecha de calendario. Un caso puede estar cómodo de SLA y
                  vencido de fecha —porque se prometió corto— y al revés.
                </>
              }
            />
          }
        />
      </div>

      {/* **Lo que se va a vencer, que es lo que hace falta para armar la semana.** Había «ya
          venció» y «vence hoy», y nada entre eso y el futuro: para repartir trabajo el lunes,
          «hoy» llega tarde. Va con su ventana al lado — «12 vencen pronto» no significa nada sin
          decir hasta cuándo. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="executive-vencen-pronto">
        <KpiCard
          label="Vencen esta semana"
          value={aging?.vencen_pronto_total ?? '—'}
          sub={aging?.vencen_pronto_hasta
            ? `próximos ${aging.vencen_pronto_dias_habiles} días hábiles, hasta el ${aging.vencen_pronto_hasta}`
            : 'no se pudo calcular la ventana: revisá el horario del área'}
          danger={(aging?.vencen_pronto_total || 0) > 0}
          ayuda={
            <ComoSeCalcula
              testId="ayuda-vencen-pronto"
              pregunta="¿Qué tengo que repartir esta semana para no quedar mal con nadie?"
              cuenta={
                <>
                  Casos abiertos cuya fecha comprometida cae entre hoy y el fin de los próximos{' '}
                  {aging?.vencen_pronto_dias_habiles} días <b>hábiles</b> del área
                  {aging?.vencen_pronto_hasta ? ` — el ${aging.vencen_pronto_hasta}` : ''}.
                </>
              }
              universo="Casos abiertos con fecha comprometida. Los que ya vencieron están en «Vencidos (fecha)», no acá: son dos decisiones distintas."
              parametro={dice.horario}
              ojo="Es un contador y no un filtro de la bandeja a propósito: filtrar «en riesgo» cuesta el índice con el que la bandeja se ordena por compromiso, y ese orden es lo que la hace servir. La bandeja ya pone estos casos arriba."
            />
          }
        />
      </div>

      {(aging?.vencen_pronto || []).length > 0 && (
        <Card className="rounded-sm border-brand-border" data-testid="executive-vencen-pronto-lista">
          <CardHeader className="pb-2 border-b border-brand-border py-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1">
              <Clock className="w-3 h-3 text-brand-warning" />
              Vencen esta semana ({aging.vencen_pronto_total})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 text-xs">
            <div className="max-h-40 overflow-y-auto space-y-1">
              {aging.vencen_pronto.map((c) => (
                <div key={c.ticket_id} className="flex justify-between gap-2">
                  <span className="truncate" title={c.title}>
                    <span className="font-mono text-zinc-400 mr-1">{c.ticket_id}</span>{c.title}
                  </span>
                  <Badge variant="outline" className="text-[9px] rounded-sm shrink-0">
                    {String(c.fecha_hasta).slice(0, 10)}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan vs compromiso. Va **al lado** del semáforo y no adentro: el semáforo es
          reactivo —cuántas horas lleva el reloj— y esto es anticipado —con el plan que hay,
          ¿se llega?—. Un caso puede estar en verde y ya estar perdido. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="executive-plan">
        <KpiCard
          label="No entra con el plan actual"
          value={plan?.resumen?.no_entra ?? '—'}
          sub="el plan termina después de lo prometido"
          danger={(plan?.resumen?.no_entra || 0) > 0}
        />
        <KpiCard
          label="Días de atraso a resolver"
          value={plan?.resumen?.dias_de_atraso ?? '—'}
          sub="días hábiles que hay que sacar de algún lado"
          danger={(plan?.resumen?.dias_de_atraso || 0) > 0}
        />
        <KpiCard
          label="Sin plan"
          value={plan?.resumen?.sin_plan ?? '—'}
          sub="abiertos sin ninguna tarea con fecha"
        />
        <KpiCard
          label="Esperando estimación"
          value={plan?.esperando_estimacion?.total ?? '—'}
          sub={
            plan?.esperando_estimacion?.total
              ? `el más viejo hace ${plan.esperando_estimacion.mas_viejo_dias} días hábiles`
              : 'no prometen cierre hasta analizarse'
          }
        />
      </div>

      {/* **La alerta que llega a tiempo.** Las cuatro de arriba miran fechas: avisan cuando el
          compromiso ya no entra. Esta mira el presupuesto de horas contra lo que se declaró hecho,
          y por eso aparece antes — mientras todavía se puede re-estimar, repartir o recortar.
          Es por **tarea** y no por caso, así que solo respeta el filtro de cápsula; lo dice el sub. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="executive-desvio-en-curso">
        <KpiCard
          label="Gastaron más de lo que avanzaron"
          value={desvioEnCurso?.total ?? '—'}
          sub={
            desvioEnCurso
              ? `medible en ${desvioEnCurso.medible_en} de ${desvioEnCurso.abiertas} tareas abiertas · toda la cápsula`
              : 'tareas abiertas, por horas contra avance declarado'
          }
          danger={(desvioEnCurso?.total || 0) > 0}
          ayuda={
            <ComoSeCalcula
              testId="ayuda-desvio-en-curso"
              pregunta="¿Qué tarea se está yendo de mano, mientras todavía se puede hacer algo?"
              cuenta="Por tarea abierta: las horas imputadas sobre las estimadas, contra el avance que declaró quien la trabaja. Si gastó el 100 % y declaró el 20 %, la brecha es de 80 puntos."
              universo={
                <>
                  {desvioEnCurso?.medible_en ?? 0} de {desvioEnCurso?.abiertas ?? 0} tareas abiertas.
                  Hacen falta las <b>tres</b> cosas: estimación, horas imputadas y avance declarado.
                  Sin alguna, la tarea no se puede medir y no cuenta — ni a favor ni en contra.
                </>
              }
              parametro={
                <>
                  El avance lo declara quien trabaja la tarea, en <b>Mi trabajo</b>. Las horas salen
                  de las imputaciones. La estimación la carga quien maneja el plan.
                </>
              }
              ojo={
                <>
                  Es por <b>tarea</b> y no por caso, así que de los filtros de arriba respeta sólo
                  la cápsula. Y un <b>0</b> acá puede significar «todo bien» o «nadie declaró
                  nada»: por eso al lado va sobre cuántas se pudo medir.
                </>
              }
            />
          }
        />
      </div>

      {(desvioEnCurso?.tareas || []).length > 0 && (
        <Card className="rounded-sm border-brand-border" data-testid="executive-desvio-en-curso-lista">
          <CardHeader className="pb-2 border-b border-brand-border py-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-brand-danger" />
              Gastaron más de lo que avanzaron ({desvioEnCurso.total})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 text-xs">
            <p className="text-[11px] text-zinc-500 mb-2">
              Horas imputadas contra el avance que declaró quien trabaja la tarea. Si la tarea estaba
              mal estimada, re-estimarla deja registrado el motivo y la estimación original.
            </p>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {desvioEnCurso.tareas.map((t) => (
                <div key={t.task_id} className="flex justify-between gap-2 items-baseline">
                  <span className="truncate" title={t.title}>
                    <span className="font-mono text-zinc-400 mr-1">{t.ticket_id}</span>
                    {t.title}
                    {t.assignee?.name && (
                      <span className="text-zinc-400"> · {t.assignee.name}</span>
                    )}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-zinc-500">
                    {t.consumed_hours} / {t.estimate_hours} h · {t.avance_pct} %
                  </span>
                  <Badge variant="outline" className="text-[9px] rounded-sm shrink-0 text-brand-danger border-red-200">
                    +{t.brecha_pct} pts
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {((plan?.casos || []).some((c) => c.estado === 'no_entra')
        || (plan?.esperando_estimacion?.casos || []).length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <Card className="rounded-sm border-brand-border" data-testid="executive-no-entra">
            <CardHeader className="pb-2 border-b border-brand-border py-2">
              <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-brand-danger" /> No entra ({plan?.resumen?.no_entra || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 text-xs">
              <div className="max-h-40 overflow-y-auto space-y-1">
                {(plan?.casos || []).filter((c) => c.estado === 'no_entra').map((c) => (
                  <div key={c.ticket_id} className="flex justify-between gap-2">
                    <span className="truncate" title={c.title}>
                      <span className="font-mono text-zinc-400 mr-1">{c.ticket_id}</span>{c.title}
                    </span>
                    <Badge variant="outline" className="text-[9px] rounded-sm shrink-0 text-brand-danger border-red-200">
                      {Math.abs(c.margen_dias)} d tarde
                    </Badge>
                  </div>
                ))}
                {!(plan?.casos || []).some((c) => c.estado === 'no_entra') && (
                  <p className="text-zinc-400">Todo lo planificado entra en lo prometido.</p>
                )}
              </div>
              {(plan?.resumen?.compromisos_no_calculables || 0) > 0 && (
                <p className="mt-2 text-[10px] text-amber-700">
                  {plan.resumen.compromisos_no_calculables} caso(s) no tienen compromiso porque
                  no se pudo calcular. Eso no es "sin SLA": es configuración a revisar.
                </p>
              )}
            </CardContent>
          </Card>

          <Card className="rounded-sm border-brand-border" data-testid="executive-esperando-estimacion">
            <CardHeader className="pb-2 border-b border-brand-border py-2">
              <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1">
                <Clock className="w-3 h-3" /> Esperando estimación ({plan?.esperando_estimacion?.total || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 text-xs">
              {/* Estos casos caen en `sin_reloj` y ahí se leen como una falla. No lo son: la
                  estimación es un entregable, no un dato de entrada. Lo que sí importa de esta
                  cola es hace cuánto espera cada uno. */}
              <div className="max-h-40 overflow-y-auto space-y-1">
                {(plan?.esperando_estimacion?.casos || []).map((c) => (
                  <div key={c.ticket_id} className="flex justify-between gap-2">
                    <span className="truncate" title={c.title}>
                      <span className="font-mono text-zinc-400 mr-1">{c.ticket_id}</span>{c.title}
                    </span>
                    <Badge variant="outline" className="text-[9px] rounded-sm shrink-0">
                      {c.dias_esperando} d
                    </Badge>
                  </div>
                ))}
                {(plan?.esperando_estimacion?.casos || []).length === 0 && (
                  <p className="text-zinc-400">Ninguno esperando estimación.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Las que esperan, ordenadas por cuánto llevan. Es la lista accionable: el resto del
          tablero dice cómo venimos, esto dice a quién llamar primero. */}
      {(aging?.sla_atencion?.esperando || []).length > 0 && (
        <Card className="rounded-sm border-brand-border" data-testid="executive-esperando">
          <CardHeader className="pb-2 border-b border-brand-border py-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1">
              <Clock className="w-3 h-3" /> Esperando atención ({aging.sla_atencion.esperando_total})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-44 overflow-y-auto">
              {aging.sla_atencion.esperando.map((e) => (
                <div key={e.ticket_id} className="flex items-center gap-2 px-3 py-1.5 border-b border-zinc-100 text-[11px]">
                  <span className="font-mono text-[10px] text-brand-primary shrink-0">{e.ticket_id}</span>
                  <span className="truncate flex-1 text-zinc-600">{e.title}</span>
                  <Badge
                    variant="outline"
                    className={`text-[9px] rounded-sm shrink-0 ${e.sla_atencion_horas && e.horas_esperando >= e.sla_atencion_horas ? 'text-brand-danger border-red-200' : ''}`}
                  >
                    {e.horas_esperando}h{e.sla_atencion_horas ? ` / ${e.sla_atencion_horas}h` : ' · sin reloj'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* **Los dos mostraban el mismo numero con el filtro por omision.** El alcance «Abiertos»
            ya recorta el universo, asi que `total` y `open` dan lo mismo y quedaban lado a lado
            repitiendose — con el preset «Criticos» pasaba igual con `total` y `critical`. Ahora el
            primero dice **sobre que universo** se esta mirando, que es lo que falta para leer el
            resto de la fila. */}
        <Kpi label={`${unidadDeTrabajo(unidad, { plural: true })} en la vista`} value={summary?.total ?? '—'} />
        <Kpi label="De esos, abiertos" value={summary?.open ?? '—'} />
        <Kpi label="Criticos/Altos" value={summary?.critical ?? '—'} danger={summary?.critical > 0} />
        {/* **El denominador al lado del numero.** "Estimado 40 / Real 62" parece un 55 % de
            sobrecosto, y puede ser que la mitad de los casos no tenga estimacion: sus horas
            consumidas estan en el numerador y no en el denominador. El panel de Desvio ya lo hace
            bien; este resumen no lo hacia. */}
        <Kpi label="Estimado (h)" value={summary?.estimated_effort_hours ?? '—'}
          sub={summary?.sin_estimacion
            ? `${summary.esfuerzo_sobre} de ${summary.total} estimados`
            : undefined} />
        <Kpi
          label="Real (h)"
          value={summary?.consumed_effort_hours ?? '—'}
          ayuda={
            <ComoSeCalcula
              testId="ayuda-real-horas"
              pregunta="¿Cuántas horas se trabajaron de verdad en estos casos?"
              cuenta="La suma de las imputaciones confirmadas de todas sus tareas."
              universo={
                <>
                  <b>Todos</b> los casos de la vista, también los que no tienen estimación. Por eso
                  no se puede dividir contra «Estimado (h)» de al lado: el numerador tiene casos que
                  el denominador no.
                </>
              }
              parametro="Las horas las carga cada persona desde la tarea o desde el caso, con una nota obligatoria."
            />
          }
        />
        {/* **Un guion es honesto; un cero es una afirmacion.** Esto mostraba `0%` cuando el
            promedio venia vacio **y** cuando el area no habia planificado nada: un `?? 0` sobre un
            promedio que ya promediaba ceros. Ahora el servidor manda `null` si no hay nada medido, y
            dice sobre cuantos casos se midio. */}
        <Kpi label="Avance medio"
          value={summary?.avg_progress_pct == null ? '—' : `${summary.avg_progress_pct}%`}
          sub={summary?.avg_progress_pct == null
            ? 'ningún caso tiene avance medido'
            : (summary?.sin_avance
                ? `sobre ${summary.avg_progress_sobre} de ${summary.total}`
                : undefined)}
          ayuda={
            <ComoSeCalcula
              testId="ayuda-avance-medio"
              pregunta="¿Cómo viene el trabajo de los casos que miro?"
              cuenta="El promedio del avance de cada caso. El avance de un caso pondera por horas estimadas el avance que quien trabaja declaró en cada tarea; si no tiene tareas, sale de sus fases, y si tampoco, de lo que la cápsula declaró para su estado."
              universo={
                <>
                  {summary?.avg_progress_sobre ?? 0} de {summary?.total ?? 0} casos.
                  {(summary?.sin_avance || 0) > 0 && (
                    <>
                      {' '}Quedan afuera <b>{summary.sin_avance}</b> sin avance medido: promediarlos
                      como cero bajaría el número sin que nadie haya dejado de trabajar.
                    </>
                  )}
                </>
              }
              parametro={
                <>
                  El avance de cada tarea lo declara quien la trabaja. El de un estado se configura
                  en <b>Configuración de Cápsula → estados</b>, y vacío significa «no se sabe».
                </>
              }
            />
          } />
      </div>

      {/* Cola */}
      <Card className="rounded-sm border-brand-border" data-testid="executive-queue">
        <CardHeader className="pb-2 border-b border-brand-border py-2">
          <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">
            Cola de {unidadDeTrabajo(unidad, { plural: true }).toLowerCase()}
          </CardTitle>
          <p className="text-[10px] text-zinc-500 font-normal normal-case">
            {filters.capsule_id
              ? 'Prioridad inline, última nota y historial. Click en el id abre el expediente.'
              : 'Elegí una capsula para ver la cola con catálogos.'}
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {!filters.capsule_id ? (
            <p className="text-[11px] text-zinc-400 p-3">Sin capsula seleccionada.</p>
          ) : queueLoading ? (
            <div className="p-4 flex items-center gap-2 text-xs text-zinc-400"><Loader2 className="w-3 h-3 animate-spin" /> Cargando cola...</div>
          ) : queue.length === 0 ? (
            <p className="text-[11px] text-zinc-400 p-3">Sin {unidadDeTrabajo(unidad, { plural: true }).toLowerCase()} para estos filtros.</p>
          ) : (
            <div className="max-h-[28rem] overflow-y-auto">
              <table className="w-full text-[11px] table-fixed">
                <thead className="bg-zinc-50 border-b border-brand-border sticky top-0">
                  <tr>
                    <th className="text-left p-2 w-[28%]">{unidadDeTrabajo(unidad)}</th>
                    <th className="text-left p-2 w-[10%]">Estado</th>
                    <th className="text-left p-2 w-[12%]">Prioridad</th>
                    <th className="text-left p-2 w-[12%]">Gerencia</th>
                    <th className="text-right p-2 w-[8%]">Edad</th>
                    <th className="text-left p-2">Última nota</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map((t) => {
                    const hrs = ageHours(t.created_at);
                    const last = t.last_management_note;
                    const hist = noteHistory(t);
                    return (
                      <tr key={t.ticket_id} className="border-b border-zinc-100 align-top hover:bg-zinc-50/80">
                        <td className="p-2">
                          <Link
                            className="font-mono text-brand-primary hover:underline"
                            to={`/tickets?capsule=${encodeURIComponent(t.capsule_id || filters.capsule_id)}&ticket=${encodeURIComponent(t.ticket_id)}`}
                          >
                            {t.ticket_id}
                          </Link>
                          <div className="text-zinc-600 truncate" title={t.title || ''}>{t.title}</div>
                        </td>
                        <td className="p-2">{statusLabel(t.status)}</td>
                        <td className="p-2">
                          {/* **Ofrecer lo que el backend rechaza es peor que no ofrecerlo.** Este
                              combo se dibujaba editable en toda fila, para cualquier rol: un
                              director lo movia y recibia un toast de error. Quien decide es la misma
                              funcion pura que decide el 403 —viaja en `t.puedo.clasificar`— y no una
                              lista de roles escrita aca, que es como las dos declaraciones divergen.
                              Deshabilitado **con su motivo**, no mudo. */}
                          <select
                            className="border border-brand-border rounded-sm h-7 px-1 text-[11px] bg-white max-w-full disabled:opacity-60 disabled:cursor-not-allowed"
                            value={t.priority || 'medium'}
                            disabled={t.puedo ? !t.puedo.clasificar : false}
                            title={t.puedo && !t.puedo.clasificar
                              ? 'La prioridad la cambia quien tiene el caso, un supervisor o un administrador.'
                              : undefined}
                            data-testid={`prioridad-${t.ticket_id}`}
                            onChange={(e) => patchPriority(t, e.target.value)}
                          >
                            {(priorities.length
                              ? priorities
                              : [{ id: 'low', name: 'Baja' }, { id: 'medium', name: 'Media' }, { id: 'high', name: 'Alta' }, { id: 'critical', name: 'Critica' }]
                            ).map((p) => (
                              <option key={p.id} value={p.id}>{p.name || p.id}</option>
                            ))}
                          </select>
                        </td>
                        <td className="p-2 truncate" title={t.requesting_department || ''}>{t.requesting_department || '—'}</td>
                        <td className="p-2 text-right font-mono text-zinc-500">{hrs != null ? `${hrs}h` : '—'}</td>
                        <td className="p-2">
                          {last?.text ? (
                            <div className="mb-1">
                              <div className="text-zinc-700 line-clamp-2" title={last.text}>{last.text}</div>
                              <div className="text-[9px] text-zinc-400">
                                {last.by || '—'} · {last.at ? fechaYHora(last.at, '') : ''}
                              </div>
                            </div>
                          ) : (
                            <div className="text-zinc-400 text-[10px] mb-1">Sin notas</div>
                          )}
                          {expandedId === t.ticket_id ? (
                            <div className="flex flex-col gap-1">
                              <Input
                                className="h-7 text-[11px] rounded-sm"
                                placeholder={`Nota (máx ${NOTE_MAX})…`}
                                maxLength={NOTE_MAX}
                                value={noteDrafts[t.ticket_id] || ''}
                                onChange={(e) => setNoteDrafts((d) => ({ ...d, [t.ticket_id]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') addNote(t); }}
                              />
                              <div className="flex gap-1 items-center">
                                <span className="text-[9px] text-zinc-400">{(noteDrafts[t.ticket_id] || '').length}/{NOTE_MAX}</span>
                                <Button size="sm" className="h-7 text-[10px] rounded-sm px-2" onClick={() => addNote(t)}>OK</Button>
                                <Button size="sm" variant="ghost" className="h-7 text-[10px] px-1" onClick={() => setExpandedId(null)}>×</Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button type="button" className="text-brand-primary hover:underline text-[10px]" onClick={() => setExpandedId(t.ticket_id)}>
                                + Nota
                              </button>
                              {hist.length > 0 && (
                                <button
                                  type="button"
                                  className="text-zinc-500 hover:underline text-[10px]"
                                  onClick={() => setHistoryId(historyId === t.ticket_id ? null : t.ticket_id)}
                                >
                                  Historial ({hist.length})
                                </button>
                              )}
                            </div>
                          )}
                          {historyId === t.ticket_id && hist.length > 0 && (
                            <ul className="mt-1 space-y-1 border-t border-zinc-100 pt-1 max-h-28 overflow-y-auto">
                              {hist.map((n, i) => (
                                <li key={i} className="text-[10px] text-zinc-600">
                                  <span className="text-zinc-400">{n.by} · {n.at ? fechaYHora(n.at, '') : ''}</span>
                                  <div>{n.text}</div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Distribución */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard
          title="Por gerencia"
          subtitle="Casos por gerencia, y el esfuerzo estimado contra el consumido. El filtro de gerencia no se aplica acá: el eje es la gerencia."
          data-testid="executive-por-gerencia"
        >
          {byDept.length === 0 ? (
            <p className="text-[11px] text-zinc-400">Sin datos.</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={Math.max(180, byDept.length * 28)}>
                <BarChart data={byDept} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="department" tick={{ fontSize: 10 }} width={110} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F4F4F5' }} />
                  <Bar dataKey="count" name="Unidades" radius={[0, 2, 2, 0]}>
                    {byDept.map((_, i) => <Cell key={i} fill={colorDeSerie(i)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* **El subtítulo prometía el esfuerzo y el gráfico dibujaba sólo el volumen.** Los
                  tres números —estimado, consumido y avance medio— vienen del backend desde que
                  este corte existe y la pantalla los tiraba. Van en una tabla y no como barras
                  apiladas: horas y casos no comparten eje, y apilarlos daría una barra que no
                  significa nada. */}
              <div className="overflow-x-auto mt-2 border-t border-brand-border pt-2">
                <table className="w-full text-[11px]">
                  <thead className="text-zinc-500">
                    <tr>
                      <th className="text-left p-1">Gerencia</th>
                      <th className="text-right p-1">Casos</th>
                      <th className="text-right p-1" title="Horas estimadas de los casos de esta gerencia">Est. (h)</th>
                      <th className="text-right p-1" title="Horas imputadas">Real (h)</th>
                      <th className="text-right p-1" title="Promedio del avance de los casos que lo tienen medido">Avance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byDept.map((d) => (
                      <tr key={d.department} className="border-t border-zinc-100">
                        <td className="p-1 truncate">{d.department}</td>
                        <td className="p-1 text-right font-mono">{d.count}</td>
                        <td className="p-1 text-right font-mono">{d.estimated_effort_hours ?? '—'}</td>
                        <td className="p-1 text-right font-mono">{d.consumed_effort_hours ?? '—'}</td>
                        {/* Un guion y no un cero: «ningún caso tiene avance medido» no es «avanzan 0 %». */}
                        <td className="p-1 text-right font-mono">
                          {d.avg_progress_pct == null ? '—' : `${d.avg_progress_pct} %`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </ChartCard>

        <ChartCard
          title="Distribución por estado"
          subtitle="Respeta todos los filtros de arriba, incluido el de estado."
          data-testid="executive-por-estado"
        >
          {byStatus.length === 0 ? (
            <p className="text-[11px] text-zinc-400">Sin datos.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(180, byStatus.length * 28)}>
              <BarChart
                data={byStatus.map((s) => ({ nombre: statusLabel(s.status), count: s.count }))}
                layout="vertical"
                margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                <YAxis type="category" dataKey="nombre" tick={{ fontSize: 10 }} width={110} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F4F4F5' }} />
                <Bar dataKey="count" name="Unidades" radius={[0, 2, 2, 0]}>
                  {byStatus.map((_, i) => <Cell key={i} fill={colorDeSerie(i)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="mt-3 pt-3 border-t border-zinc-100">
            {/* Ignora el filtro de prioridad a proposito —el eje **es** la prioridad— y es
                obvio por el grafico, pero decirlo cuesta una linea y evita que alguien concluya
                que el filtro no funciona. */}
            <div className="text-[10px] uppercase text-zinc-400 mb-1" title="El filtro de prioridad no se aplica acá: el eje es la prioridad.">
              Prioridad <span className="normal-case text-zinc-300">· sin filtrar por prioridad</span>
            </div>
            {byPriority.map((p) => (
              <div key={p.priority} className="flex justify-between text-[11px]">
                <span>{priorityLabel(p.priority)}</span><span className="font-mono">{p.count}</span>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Antigüedad + SLA */}
        <Card className="rounded-sm border-brand-border">
          <CardHeader className="pb-2 border-b border-brand-border py-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1"><Clock className="w-3 h-3" /> Antiguedad y SLA</CardTitle>
          </CardHeader>
          <CardContent className="p-3 space-y-3 text-[11px]">
            <div className="flex gap-2">
              {(aging?.age_buckets || []).map((b) => (
                <div key={b.bucket} className="flex-1 text-center border border-zinc-100 rounded-sm p-2">
                  <div className="font-bold text-base">{b.count}</div>
                  <div className="text-[9px] text-zinc-400">{b.bucket}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2 text-center">
              <div className="flex-1 border border-zinc-100 rounded-sm p-2">
                <div className="font-bold">{sla.ok ?? '—'}</div>
                <div className="text-[9px] text-zinc-400">SLA OK</div>
              </div>
              <div className="flex-1 border border-amber-100 rounded-sm p-2">
                <div className="font-bold text-amber-700">{sla.risk ?? '—'}</div>
                <div className="text-[9px] text-zinc-400">Riesgo ≥80%</div>
              </div>
              <div className="flex-1 border border-red-100 rounded-sm p-2">
                <div className="font-bold text-brand-danger">{sla.breach ?? '—'}</div>
                {/* **El mismo nombre que el KPI de arriba, porque es el mismo numero.** Se llamaba
                    «Fuera SLA» aca, «Fuera de SLA de resolucion» arriba y «Fuera de SLA (N)» en la
                    lista: tres rotulos para `aging.sla.breach` en la misma pestaña, y entonces hay
                    que mirar dos veces para darse cuenta de que no son tres cosas. */}
                <div className="text-[9px] text-zinc-400">Fuera de SLA de resolución</div>
              </div>
            </div>
            <div>
              {/* El detalle del mismo numero, y lo dice: «cuáles». Y el total va al lado del
                  largo de la lista, que viene recortada a 50. */}
              <div className="text-[10px] uppercase text-zinc-400 mb-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-brand-danger" />
                Fuera de SLA de resolución: cuáles
                {(sla.breach_tickets_total || 0) > (sla.breach_tickets?.length || 0) && (
                  <span className="normal-case text-zinc-400">
                    ({sla.breach_tickets.length} de {sla.breach_tickets_total})
                  </span>
                )}
              </div>
              <div className="max-h-28 overflow-y-auto space-y-1">
                {(sla.breach_tickets || []).map((t) => (
                  <div key={t.ticket_id} className="flex justify-between gap-2">
                    <span className="truncate">{t.title}</span>
                    <Badge variant="outline" className="text-[9px] rounded-sm shrink-0">{t.age_hours}h / {t.sla_hours}h</Badge>
                  </div>
                ))}
                {(!sla.breach_tickets || sla.breach_tickets.length === 0) && <p className="text-zinc-400">Ninguno fuera de SLA.</p>}
              </div>
            </div>
            <div>
              {/* Se llamaba "Vencidos por due_date": el nombre del campo de la base, en la UI, sobre
                  una lista que estaba vacia siempre porque nadie escribe ese campo. */}
              <div className="text-[10px] uppercase text-zinc-400 mb-1">
                Vencidos (fecha): cuáles
                {(aging?.overdue_total || 0) > (aging?.overdue?.length || 0) && (
                  <span className="normal-case text-zinc-400">
                    {' '}({aging.overdue.length} de {aging.overdue_total})
                  </span>
                )}
              </div>
              <div className="max-h-24 overflow-y-auto space-y-1">
                {(aging?.overdue || []).map((t) => (
                  <div key={t.ticket_id} className="flex justify-between gap-2">
                    <span className="truncate">{t.title}</span>
                    <Badge variant="outline" className="text-[9px] rounded-sm shrink-0">
                      {String(t.fecha_hasta || '').slice(0, 10)}
                    </Badge>
                  </div>
                ))}
                {(!aging?.overdue || aging.overdue.length === 0) && <p className="text-zinc-400">Sin vencidos.</p>}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-sm border-brand-border">
          <CardHeader className="pb-2 border-b border-brand-border py-2">
            <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em] flex items-center gap-1"><Users className="w-3 h-3" /> Carga por persona</CardTitle>
            <ComoSeCalcula
              testId="ayuda-carga-por-persona"
              pregunta="¿A quién le puedo dar el próximo trabajo?"
              cuenta="Por persona: cuántas tareas abiertas tiene, y la suma de las horas estimadas e imputadas de esas tareas. Lo ya cerrado va en la última columna y no cuenta como carga."
              universo="Tareas de los casos que pasan los filtros de arriba, asignadas a personas. Las de agentes y las del sistema no cuentan."
              parametro="Para ver los días concretos y quién queda sobreasignado, hacé clic en una persona: abre su Gantt con la franja de carga, que usa su horario y su disponibilidad."
              ojo="Las horas son del plan, no del calendario: dos tareas de 8 h la misma semana suman 16 h acá aunque una esté planificada para el mes que viene. La franja del Gantt es la que mira día por día."
            />
          </CardHeader>
          <CardContent className="p-0">
            {workload.length === 0 ? <p className="text-[11px] text-zinc-400 p-3">Sin datos.</p> : (
              <table className="w-full text-[11px]">
                <thead className="bg-zinc-50 border-b border-brand-border">
                  {/* **Las horas son de las abiertas.** Sumaban todas las tareas de la persona,
                      terminadas incluidas, debajo de un encabezado que dice «abiertas»: alguien con
                      años de trabajo cerrado figuraba con mil horas de carga, y la única pantalla
                      que sirve para repartir no servía para eso. Lo cerrado va aparte. */}
                  <tr>
                    <th className="text-left p-2">Persona</th>
                    <th className="text-right p-2">Tareas abiertas</th>
                    <th className="text-right p-2" title="Horas estimadas de sus tareas abiertas">Est. abiertas (h)</th>
                    <th className="text-right p-2" title="Horas imputadas a sus tareas abiertas">Real abiertas (h)</th>
                    <th className="text-right p-2" title="Lo ya terminado, que no es carga: está acá para que el número de al lado no parezca bajo">Ya cerradas</th>
                  </tr>
                </thead>
                <tbody>
                  {workload.map((w) => (
                    <tr
                      key={w.person_id}
                      className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 cursor-pointer"
                      onClick={() => openPersonGantt(w.person_id)}
                      data-testid={`workload-person-${w.person_id}`}
                    >
                      <td className="p-2 text-brand-primary underline-offset-2 hover:underline">{w.person_name}</td>
                      <td className="p-2 text-right font-mono">{w.open_tasks}</td>
                      <td className="p-2 text-right font-mono">{w.estimated}</td>
                      <td className="p-2 text-right font-mono">{w.consumed}</td>
                      <td className="p-2 text-right font-mono text-zinc-400">
                        {w.closed_tasks ?? 0}
                        {w.consumed_cerradas ? ` · ${w.consumed_cerradas} h` : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="analisis" className="mt-4">
          <PanelAnalisis params={analyticsParams()} unidadPlural={unidadDeTrabajo(unidad, { plural: true })} />
        </TabsContent>

        <TabsContent value="desvio" className="mt-4">
          <PanelDesvio
            datos={desvio}
            loading={desvioLoading}
            unidadPlural={unidadDeTrabajo(unidad, { plural: true })}
          />
        </TabsContent>

        <TabsContent value="categorias" className="mt-4">
          {/* La pantalla de categorías, adentro: era una página aparte a la que se llegaba
              por un botón, y el corte por categoría es parte de mirar el tablero. */}
          <CategoriasPage embebido capsuleId={filters.capsule_id} department={filters.department} />
        </TabsContent>

        <TabsContent value="prioridades" className="mt-4">
          {/* La lista de prioridad de la empresa, que arma Dirección (2026-10-09). Ver el componente. */}
          <PrioridadDeLaEmpresa tenantId={tenantId} capsuleId={filters.capsule_id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
